import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { LazyLoadImage } from 'react-lazy-load-image-component'
import { Stack, Typography } from '@mui/material'
import PropTypes from 'prop-types'
import { DeleteButton, Dropzone } from '~/components'
import { acceptedImageTypes, acceptedVideoTypes } from '../data'
import Alt from './Alt'
import Field from './Field'
import { useParagraphContext } from './hooks'

const Image = ({
  alt,
  highlighted = false,
  movie = false,
  placeholder,
  showDelete = false,
  type = 'main',
}) => {
  const { t } = useTranslation()

  const { editable, paragraph, uploadImage, deleteImage } = useParagraphContext(
    Image.name
  )

  // const [loaded, setLoaded] = useState(false)
  const source = type === 'icon' ? paragraph.icon : paragraph.media
  const [imageError, setImageError] = useState()
  useEffect(() => {
    queueMicrotask(() => {
      setImageError()
      // setLoaded(false)
    })
  }, [source])

  const imageType = type !== 'icon' ? 'media' : undefined

  return (
    <Stack spacing={0.25}>
      <Field
        name={imageType}
        render={() => (
          <Dropzone
            accept={
              type !== 'icon' && movie
                ? { ...acceptedImageTypes, ...acceptedVideoTypes }
                : acceptedImageTypes
            }
            maxFileSize={600 * 1024}
            multiple={false}
            sx={{
              '& .notchedOutline': {
                border: (theme) =>
                  highlighted
                    ? `2px solid ${theme.palette.input.border.highlighted}`
                    : `1px solid ${theme.palette.input.border.default}`,
              },
              minHeight: '80px',
              ...(source && !imageError
                ? {
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    fontSize: 0,
                    p: 0,
                    '& img': {
                      objectFit: 'contain',
                      width: '100%',
                      height: '100%',
                    },
                  }
                : { px: 1.5, py: 1 }),
              overflow: 'clip',
              flexGrow: 1,
            }}
            onDrop={async (files) => {
              setImageError()
              await uploadImage(type, files[0])
            }}
          >
            {imageError ? (
              <Typography sx={{ color: 'danger' }} variant="body">
                {imageError}
              </Typography>
            ) : source ? (
              type !== 'icon' && source.endsWith('.mp4') ? (
                <video
                  src={source}
                  autoPlay
                  muted
                  loop
                  style={{ width: '100%', height: '100%' }}
                />
              ) : (
                <LazyLoadImage
                  alt={paragraph.alt}
                  src={source}
                  // style={{ visibility: loaded ? 'visible' : 'hidden' }}
                  // onLoad={() => setLoaded(true)}
                  onError={() => setImageError(t('paragraph.image-error'))}
                />
              )
            ) : (
              <Typography sx={{ opacity: 0.5 }} variant="caption">
                {placeholder}
              </Typography>
            )}
            {showDelete && source && !imageError && (
              <Stack
                spacing={0.5}
                sx={{
                  p: 0.5,
                  position: 'absolute',
                  top: 0,
                  right: 0,
                }}
              >
                <DeleteButton
                  disabled={!editable}
                  onClick={async (e) => {
                    e.stopPropagation()
                    await deleteImage(type)
                  }}
                />
              </Stack>
            )}
          </Dropzone>
        )}
        sx={{ width: '100%' }}
      />
      <Alt {...alt} />
    </Stack>
  )
}

Image.propTypes = {
  alt: Alt.propTypes,
  highlighted: PropTypes.bool,
  movie: PropTypes.bool,
  placeholder: PropTypes.node,
  showDelete: PropTypes.bool,
  type: PropTypes.string,
}

export default Image
